'use strict';

import { hot } from 'shared/hot'; // Must be required before React
import React, { useState } from 'react';
import Downshift from 'downshift';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons/faCircleNotch';
import { postJson } from 'shared/fetch.js';
import route from 'shared/ziggy.js';

function SearchPanel({ loading, engine, onHide }) {
	const [results, setResults] = useState([]);
	const [querying, setQuerying] = useState(false);
	
	if (loading) {
		return (
			<div className="antialiased m-4">
				Loading…
			</div>
		);
	}
	
	const onInput = (term) => {
		if ('' === term) {
			return;
		}
		
		setQuerying(true);
		
		const url = `https://search-api.swiftype.com/api/v1/public/installs/${ engine }/suggest.json`;
		
		postJson(url, { q: term })
			.then(result => result.json())
			.then(data => {
				const results = 'records' in data && 'page' in data.records
					? data.records.page
					: [];
				
				setResults(results);
				setQuerying(false);
			});
	};
	
	const renderDownshift = (downshift) => {
		const { getInputProps, getLabelProps, getMenuProps, inputValue, isOpen } = downshift;
		
		const show_results = isOpen && results.length > 0;
		const hide_results = true !== show_results;
		
		const label_props = getLabelProps({
			className: isOpen
				? 'sr-only'
				: "font-condensed my-4 text-lg text-center text-gray-400 text-center block"
		});
		
		const input_props = getInputProps({
			autoFocus: true,
			className: "block w-full mb-4 p-2 px-4 text-base leading-normal bg-white border rounded-full text-gray-700 border-gray-300",
			placeholder: "Start typing to search all of NACHI.ORG",
			onKeyUp: event => {
				if ('Escape' === event.key && '' === inputValue) {
					onHide();
				}
			},
		});
		
		const menu_props = getMenuProps({
			className: show_results
				? 'mt-4 pt-4 border-t list-reset overflow-y-auto overflow-x-hidden'
				: 'hidden',
			style: {
				maxHeight: `${ window.innerHeight * .75 }px`
			},
		});
		
		const renderDownshiftItem = (record, index) => {
			const { getItemProps, highlightedIndex, selectedItem } = downshift;
			const is_highlighted = highlightedIndex === index;
			
			const base_class = 'block p-2 w-full rounded cursor-pointer overflow-hidden';
			
			const props = getItemProps({
				key: record.id,
				index,
				item: record,
				className: is_highlighted
					? `bg-blue-100 ${ base_class }`
					: `bg-white ${ base_class }`,
			});
			
			const highlight = html => html.replace(/\<em\>/g, '<em class="not-italic text-blue-700 font-bold">');
			
			const title = 'title' in record.highlight
				? <span dangerouslySetInnerHTML={ { __html: highlight(record.highlight.title) } } />
				: record.title;
			
			const subtitle = 'sections' in record.highlight
				? <div className="text-sm opacity-7" dangerouslySetInnerHTML={ { __html: highlight(record.highlight.sections) } } />
				: null;
			
			return (
				<li { ...props }>
					<div className="font-condensed mb-1">
						{ title }
					</div>
					<div className="text-xs text-green-700 opacity-6 mb-1">
						{ record.url }
					</div>
					{ subtitle }
				</li>
			);
		};
		
		return (
			<div className="text-left" style={ { width: `${ window.innerWidth < 450 ? window.innerWidth : 450 }px` } }>
				<label { ...label_props }>
					Search NACHI.ORG:
				</label>
				<div className="relative">
					<input { ...input_props } />
					{ querying && (
						<div className="absolute right-0 top-0 bottom-0 flex items-center pr-4">
							<Icon icon={ faCircleNotch } className="opacity-2 svg-spin text-xl" />
						</div>
					) }
				</div>
				<ul { ...menu_props }>
					{ show_results && results.map(renderDownshiftItem) }
				</ul>
				{ hide_results && (
					<div className="mt-8 pt-4 border-t">
						<p className="text-center font-sans text-sm text-gray-300">
							Other search options:
						</p>
						<div className="flex mt-1 justify-center">
							<a className="btn btn-blue btn-link mx-1" href="https://forum.nachi.org/search" target="_blank">
								Search the Forum
							</a>
							<a className="btn btn-blue btn-link mx-1" href={ route('listings.index') } target="_blank">
								Find a Certified Inspector
							</a>
							<a className="btn btn-blue btn-link mx-1 ml-auto" href={ route('frontend.search.index') }>
								More…
							</a>
						</div>
					</div>
				) }
			</div>
		);
	};
	
	return (
		<div className="tailwind p-2">
			<Downshift
				onChange={ selection => {
					window.location.href = selection.url;
				} }
				onInputValueChange={ onInput }
				itemToString={ item => (item ? item.title : '') }
				children={ renderDownshift }
			/>
		</div>
	);
}

export default hot(SearchPanel);
